import { IonInput, IonRow, IonSpinner, IonText } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import sha256 from 'crypto-js/sha256';
import {
  selectCrateState,
  selectSelectedCrate,
  updateTrackField,
} from '../../store/slices/crate.slice';
import {
  selectAllSelectedMatches,
  setSelectedFilePaths,
} from '../../store/slices/track-matcher.slice';
import { AppDispatch } from '../../store/store';
import { iTrack } from '../../types/ITrack';

// Stable empty array reference to prevent re-renders
const emptyArray: string[] = [];

interface EditableTextFieldProps {
  trackId: string;
  text: string;
  field: keyof iTrack;
  className?: string;
  textColor?: string;
  disabled?: boolean;
}

export const EditableTextField: React.FC<EditableTextFieldProps> = ({
  trackId,
  text,
  field,
  className = '',
  textColor = 'dark',
  disabled = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isEditable } = useSelector(selectCrateState);
  const selectedCrate = useSelector(selectSelectedCrate);
  const [displayValue, setDisplayValue] = useState<string>(text);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const showEditInput =
    isEditable && (!disabled || isFocused || hasUnsavedChanges);

  const allSelectedMatches = useSelector(selectAllSelectedMatches);
  const selectedPaths = allSelectedMatches[trackId] || emptyArray;

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(text);
      setHasUnsavedChanges(false);
    }
  }, [text, isFocused]);

  const handleInput = (e: CustomEvent<{ value: string | null }>) => {
    const newValue = e.detail.value ?? '';
    setDisplayValue(newValue);

    if (newValue !== text) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  };

  const saveChanges = async () => {
    if (!hasUnsavedChanges || displayValue === text) return;

    const track = selectedCrate?.Tracks?.find((t) => t.ID === trackId);
    if (!track) return;

    const updatedArtist = field === 'Artist' ? displayValue : track.Artist;
    const updatedTitle = field === 'Title' ? displayValue : track.Title;

    setIsUpdating(true);
    try {
      if (field === 'Artist' || field === 'Title') {
        // IMPORTANT: Must use trimmed values to match hash generation in other parts of the app
        const newTrackId = sha256(
          JSON.stringify({
            artist: updatedArtist?.trim() ?? '',
            title: updatedTitle?.trim() ?? '',
          }),
        ).toString();

        const result = await dispatch(
          updateTrackField({ trackId, field, value: displayValue }),
        );

        if (updateTrackField.fulfilled.match(result)) {
          dispatch(
            updateTrackField({ trackId, field: 'ID', value: newTrackId }),
          );

          if (selectedPaths?.length > 0) {
            dispatch(
              setSelectedFilePaths({
                trackId: newTrackId,
                filePaths: selectedPaths,
              }),
            );
            dispatch(setSelectedFilePaths({ trackId: trackId, filePaths: [] }));
          }
          setHasUnsavedChanges(false);
        } else {
          console.log('Field update failed');
          setDisplayValue(text);
          setHasUnsavedChanges(false);
        }
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setDisplayValue(text);
      setHasUnsavedChanges(false);
    } finally {
      setIsUpdating(false);
    }
  };
  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = async () => {
    setIsFocused(false);
    await saveChanges();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      saveChanges();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setDisplayValue(text);
      setHasUnsavedChanges(false);
      inputRef.current?.blur();
    }
  };

  return (
    <IonRow
      className={className}
      style={{ width: '100%', position: 'relative' }}
    >
      {showEditInput ? (
        <div style={{ position: 'relative', width: '100%' }}>
          <IonInput
            ref={inputRef}
            value={displayValue}
            onIonInput={(e) =>
              handleInput(e as CustomEvent<{ value: string | null }>)
            }
            onIonFocus={handleFocus}
            onIonBlur={handleBlur}
            onKeyDown={handleKeyDown}
            label={field.toUpperCase()}
            labelPlacement="floating"
            className={className}
            color={textColor}
            style={{
              border: hasUnsavedChanges
                ? '2px solid var(--ion-color-primary)'
                : '1px solid transparent',
              borderRadius: '4px',
              opacity: isUpdating ? 0.6 : 1,
            }}
            disabled={
              isUpdating || (disabled && !isFocused && !hasUnsavedChanges)
            }
          />
          {isUpdating && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                right: '16px',
                transform: 'translateY(-50%)',
              }}
            >
              <IonSpinner
                name="crescent"
                style={{ width: '16px', height: '16px' }}
              />
            </div>
          )}
        </div>
      ) : (
        <IonText color={textColor}>
          <p>{displayValue}</p>
        </IonText>
      )}
    </IonRow>
  );
};
